'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import type { PayInResponse } from '@payments/aurum-contracts';
import { API_ROUTES, useApiInvalidate } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { cn } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { createDebug } from '@shared/debug';
import { useDataLayerTrackEvent, useFullStoryTrackEvent } from '@shared/tracking';
import { useClientConfig } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { MultistepForm } from '../../../form-controls/MultistepForm/MultistepForm';
import { AccountSuspensionGate } from '../../../my-account/AccountSuspensionGate/AccountSuspensionGate';
import { useDepositLimitsQuery } from '../../../my-account/SaferGambling/hooks/useDepositLimitsQuery';
import { DEPOSIT_PAYMENT_METHODS } from '../../../PaymentMethodSelector/PaymentMethods';
import type { MethodOrInstrument } from '../../../PaymentMethodSelector/types';
import { toast } from '../../../Toast/Toast';
import { getPayInErrorMessage } from '../utils/aurumErrorTranslation';
import { pathUtils, timers } from './DepositSheet';
import { useDepositFlow } from './hooks/useDepositFlow';
import { useDepositSheet, type DepositFormValues } from './hooks/useDepositSheet';
import { CardDetailsStep } from './steps/CardDetailsStep/CardDetailsStep';
import { DepositAmountStep } from './steps/DepositAmountStep/DepositAmountStep';
import { DepositLimitsStep } from './steps/DepositLimitsStep/DepositLimitsStep';
import { ErrorStep } from './steps/ErrorStep/ErrorStep';
import { RedirectionStep } from './steps/RedirectionStep/RedirectionStep';
import { SuccessStep } from './steps/SuccessStep/SuccessStep';
import { DEPOSIT_ERROR_STEP, type DepositSuccessData, type TrackingDetails, type TransactionStatusResponse } from './types';

const REDIRECTION_TIMEOUT_MS = 300000; // 5 minutes timeout for redirection

type DepositFlowOrchestratorProps = {
  className?: string;
  onClose: () => void;
  onStepChange?: (isFirstStep: boolean) => void;
  onStateChange?: (state: { isTransactionInProgress: boolean; isRedirectionInProgress: boolean; depositErrorStep: keyof typeof DEPOSIT_ERROR_STEP | null }) => void;
  onBackHandlerReady?: (handler: () => void) => void;
  onSuccessCallback?: (depositData?: DepositSuccessData) => void;
  minimumAmountForBonus?: number;
  initialAmount?: string;
  redirectUrlOnInsufficientDeposit?: string;
  autoProcessPaymentMethod?: MethodOrInstrument;
  skipSuccessStep?: boolean;
  redirectUrlOnSuccess?: string;
  openOnDepositLimits?: boolean;
  onStepIdChange?: (stepId: string | null) => void;
};

function DepositFlowOrchestrator({
  className,
  onClose,
  onStepChange,
  onStateChange,
  onBackHandlerReady,
  onSuccessCallback,
  minimumAmountForBonus,
  initialAmount,
  redirectUrlOnInsufficientDeposit,
  autoProcessPaymentMethod,
  skipSuccessStep = false,
  redirectUrlOnSuccess,
  openOnDepositLimits = false,
  onStepIdChange,
}: DepositFlowOrchestratorProps) {
  const { suspendedNotificationNamespaces } = getSiteConfig().MY_ACCOUNT_SETTINGS;
  const debug = createDebug({ isEnabled: false });
  const t = useTranslations('global_sheets.deposit');
  const [isFirstStep, setIsFirstStep] = useState(true);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<string>('');
  const [depositErrorMessage, setDepositErrorMessage] = useState<string | null>(null);
  const [depositErrorKey, setDepositErrorKey] = useState<string | undefined>(undefined);
  const [depositErrorStep, setDepositErrorStep] = useState<keyof typeof DEPOSIT_ERROR_STEP | null>(null);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const isTransactionInProgressRef = useRef(false);
  const isRedirectionInProgressRef = useRef(false);
  const backHandlerRef = useRef<(() => boolean) | null>(null);
  const cleanupHandlerRef = useRef<(() => void) | null>(null);
  const trackingRef = useRef<{ provider?: string; amount?: string; transactionId?: string; method?: string; offer?: string } | null>(null);
  const skipToStepRef = useRef<((stepId: string) => void) | null>(null);
  const setStatusRef = useRef<((status: string) => void) | null>(null);
  const redirectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const redirectionTriggeredRef = useRef<boolean>(false);
  const handleSuccessRef = useRef<((transactionStatus: TransactionStatusResponse) => void) | null>(null);
  const handleCloseRef = useRef<(() => void) | null>(null);
  const { amountSchema, getMethodSchema, createFormSchema, getAmountStepInitialValues, getMethodStepInitialValues } = useDepositSheet();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const bundleCode = searchParams.get('code');
  const isOnGamesPage = pathUtils.isGamesPage(pathname);
  const { session } = useSession();
  const playerId = session?.profile?.id;
  const cardholderNameFromSession = session?.profile ? `${session.profile.firstName ?? ''} ${session.profile.lastName ?? ''}`.trim() : '';

  const dataLayerTrackEvent = useDataLayerTrackEvent();
  const fullStoryTrackEvent = useFullStoryTrackEvent();
  const { currency } = useClientConfig();
  const { invalidateQuery } = useApiInvalidate();

  const formSchema = useMemo(() => createFormSchema(currentPaymentMethod), [currentPaymentMethod, createFormSchema]);
  const autoProcessMethodIdentifier = autoProcessPaymentMethod?.apiReference ?? autoProcessPaymentMethod?.name ?? '';
  const getAmountStepDefaultValues = useCallback(
    () => ({
      ...getAmountStepInitialValues(initialAmount),
      ...(autoProcessPaymentMethod && {
        paymentMethod: autoProcessMethodIdentifier,
        termsAccepted: true,
      }),
    }),
    [getAmountStepInitialValues, initialAmount, autoProcessPaymentMethod, autoProcessMethodIdentifier]
  );
  const form = useForm<DepositFormValues>({
    defaultValues: {
      amountStep: getAmountStepDefaultValues(),
      methodStep: getMethodStepInitialValues(currentPaymentMethod),
    },
    resolver: zodResolver(formSchema),
  });

  const { data: depositLimits, isLoading: isDepositLimitsLoading } = useDepositLimitsQuery(!!playerId);

  // Watch for payment method changes and update schema
  const watchedPaymentMethod = form.watch('amountStep.paymentMethod');

  // Track previous initialAmount to detect when it changes from undefined to a value (sheet opening)
  // or when it changes from one value to another (sheet reopening with different initial amount)
  const previousInitialAmountRef = useRef<string | undefined>(undefined);
  const previousIsFirstStepRef = useRef<boolean>(true);

  // Set form amount when initialAmount is provided (e.g., when sheet opens with new initial amount)
  // Only set it when:
  // 1. initialAmount changes from undefined to a value (sheet opening) AND we're on first step
  // 2. initialAmount changes from one value to another (sheet reopening with different initial amount) AND we're on first step
  // 3. We transition to first step AND initialAmount is defined (handles case where sheet closes on later step)
  // Not when navigating back (which preserves user-entered values)
  useEffect(() => {
    const previousInitialAmount = previousInitialAmountRef.current;
    const previousIsFirstStep = previousIsFirstStepRef.current;
    const currentFormAmount = form.getValues('amountStep.amount');

    // Update refs
    previousInitialAmountRef.current = initialAmount;
    previousIsFirstStepRef.current = isFirstStep;

    const initialAmountChanged = initialAmount !== undefined && previousInitialAmount !== initialAmount;
    const transitionedToFirstStep = !previousIsFirstStep && isFirstStep;
    const shouldSetInitialAmount = initialAmount !== undefined && isFirstStep && (initialAmountChanged || (transitionedToFirstStep && currentFormAmount === ''));

    if (shouldSetInitialAmount) {
      form.setValue('amountStep.amount', initialAmount);
    }
  }, [initialAmount, isFirstStep, form]);

  // Update resolver when payment method changes
  useEffect(() => {
    if (watchedPaymentMethod !== currentPaymentMethod) {
      setCurrentPaymentMethod(watchedPaymentMethod);
      // Only reset methodStep with proper initial values when payment method changes
      // and we're on the first step (amount step), not when user is filling out card details
      if (isFirstStep) {
        form.setValue('methodStep', getMethodStepInitialValues(watchedPaymentMethod, cardholderNameFromSession));
      }
    }
  }, [watchedPaymentMethod, currentPaymentMethod, isFirstStep, getMethodStepInitialValues, cardholderNameFromSession, form]);

  useEffect(() => {
    if (!cardholderNameFromSession) return;
    const methodStep = form.getValues('methodStep');
    if (methodStep?.cardholderName?.trim()) return;
    form.setValue('methodStep', { ...methodStep, cardholderName: cardholderNameFromSession }, { shouldDirty: false, shouldValidate: false });
  }, [cardholderNameFromSession, form]);

  // Update form resolver when schema changes
  useEffect(() => {
    form.clearErrors();
  }, [formSchema, form]);

  const getPaymentMethodKey = (paymentMethod: string): string => {
    return Object.values(DEPOSIT_PAYMENT_METHODS).find(method => method.name === paymentMethod)?.apiName || '';
  };

  const clearRedirectionTimeout = useCallback(() => {
    timers.clearTimeout(redirectionTimeoutRef.current);
    redirectionTimeoutRef.current = null;
  }, []);

  const handleTransactionComplete = useCallback(
    (transactionStatus: TransactionStatusResponse) => {
      if (isTransactionInProgressRef.current) {
        dataLayerTrackEvent({
          event: 'payin',
          payInData: {
            payInAmount: form.getValues('amountStep.amount'),
            paymentMethod: getPaymentMethodKey(currentPaymentMethod),
            currency,
            isFirstPayIn: transactionStatus.isFirstPayIn,
            daysSinceFtd: transactionStatus.daysSinceFtd,
          },
        });
        fullStoryTrackEvent('Deposit | Success', {
          method: getPaymentMethodKey(currentPaymentMethod),
          currency,
          amount: Number(form.getValues('amountStep.amount')),
          transactionId: trackingRef.current?.transactionId,
          type: transactionStatus.isFirstPayIn ? 'FTD' : 'SD',
        });
      }
      isTransactionInProgressRef.current = false;
      setDepositErrorStep(null);
      onStateChange?.({
        isTransactionInProgress: false,
        isRedirectionInProgress: false,
        depositErrorStep: null,
      });
    },
    [onStateChange, dataLayerTrackEvent, fullStoryTrackEvent, currentPaymentMethod, currency, form]
  );

  const handleError = useCallback(
    (errorMessage: string, errorStep: keyof typeof DEPOSIT_ERROR_STEP, transactionStatus?: TransactionStatusResponse, errorKey?: string) => {
      debug.forceError('Deposit error:', errorMessage);
      const resolvedErrorKey = errorKey || transactionStatus?.errorKey;
      setDepositErrorMessage(errorMessage);
      setDepositErrorKey(resolvedErrorKey);
      setDepositErrorStep(errorStep);
      clearRedirectionTimeout();

      if (isTransactionInProgressRef.current) {
        const failureType = transactionStatus != null ? (transactionStatus.isFirstPayIn ? ('FTD' as const) : ('SD' as const)) : undefined;

        fullStoryTrackEvent('Deposit | Failure', {
          method: getPaymentMethodKey(currentPaymentMethod),
          currency,
          amount: Number(form.getValues('amountStep.amount')),
          transactionId: trackingRef.current?.transactionId,
          error: resolvedErrorKey || 'aurumErrors.unknown',
          errorTranslation: errorMessage,
          ...(failureType !== undefined && { type: failureType }),
        });
      }
      isTransactionInProgressRef.current = false;
      onStateChange?.({
        isTransactionInProgress: false,
        isRedirectionInProgress: false,
        depositErrorStep: errorStep,
      });
      if (skipToStepRef.current && setStatusRef.current) {
        setStatusRef.current('error');
        skipToStepRef.current('error');
      }
    },
    [clearRedirectionTimeout, onStateChange, fullStoryTrackEvent, currentPaymentMethod, currency, form]
  );

  const startRedirectionTimeout = useCallback(() => {
    clearRedirectionTimeout();
    redirectionTimeoutRef.current = timers.setTimeout(() => {
      setIsTimedOut(true);
      handleError(t('error_step.timeout'), 'PROCESSING_DEPOSIT');
    }, REDIRECTION_TIMEOUT_MS);
  }, [clearRedirectionTimeout, handleError, t]);

  const handleSuccess = useCallback(
    (transactionStatus: TransactionStatusResponse) => {
      clearRedirectionTimeout();
      handleTransactionComplete(transactionStatus);
      invalidateQuery(API_ROUTES.MY_ACCOUNT.PAYMENT_INSTRUMENTS);

      if (playerId) {
        invalidateQuery(API_ROUTES.MY_ACCOUNT.WALLET.GET_TRANSACTIONS.replace(':playerId', playerId));
      }

      const depositAmountPence = Math.round(Number(form.getValues('amountStep.amount')) * 100);
      const isInsufficientDeposit = minimumAmountForBonus !== undefined && redirectUrlOnInsufficientDeposit && depositAmountPence < minimumAmountForBonus;

      if (isInsufficientDeposit && typeof window !== 'undefined') {
        handleCloseRef.current?.();
        window.location.href = redirectUrlOnInsufficientDeposit;
        return;
      }

      if (redirectUrlOnSuccess && typeof window !== 'undefined') {
        handleCloseRef.current?.();
        window.location.href = redirectUrlOnSuccess;
        return;
      }

      onSuccessCallback?.({
        isFirstPayIn: transactionStatus.isFirstPayIn,
        daysSinceFtd: transactionStatus.daysSinceFtd,
      });

      if (isOnGamesPage) {
        toast(
          t('success_toast', { amount: form.getValues('amountStep.amount') }),
          { type: 'success', position: 'top-right' },
          { 'data-track-type': 'Confirmation', 'data-track-action': 'Deposit Complete' }
        );
        handleCloseRef.current?.();
      } else if (!skipSuccessStep) {
        if (skipToStepRef.current && setStatusRef.current) {
          setStatusRef.current('success');
          skipToStepRef.current('success');
        }
      }
    },
    [
      clearRedirectionTimeout,
      handleTransactionComplete,
      invalidateQuery,
      playerId,
      isOnGamesPage,
      t,
      form,
      onSuccessCallback,
      minimumAmountForBonus,
      redirectUrlOnInsufficientDeposit,
      skipSuccessStep,
      redirectUrlOnSuccess,
    ]
  );

  // Set the handleSuccess ref for the deposit flow hook
  useEffect(() => {
    handleSuccessRef.current = handleSuccess;
  }, [handleSuccess]);

  // Use deposit flow hook at the orchestrator level to ensure it stays active across step changes
  const depositFlowInstance = useDepositFlow({
    namespace: 'global_sheets.deposit',
    onError: handleError,
    onSuccess: transactionStatus => handleSuccessRef.current?.(transactionStatus),
    onTransactionCreated: useCallback(
      (transactionId: string) => {
        fullStoryTrackEvent('Deposit | Pending', {
          method: getPaymentMethodKey(currentPaymentMethod),
          currency,
          amount: Number(form.getValues('amountStep.amount')),
          transactionId: transactionId,
        });
      },
      [fullStoryTrackEvent, currentPaymentMethod, currency, form]
    ),
    isTimedOut,
  });

  // Watch for mutation error state (handles cases where mutation is already in error state on mount)
  useEffect(() => {
    if (
      depositFlowInstance.createDepositTransactionMutation.isError &&
      depositFlowInstance.createDepositTransactionMutation.error &&
      skipToStepRef.current &&
      setStatusRef.current
    ) {
      const payInResponse = (depositFlowInstance.createDepositTransactionMutation.error as { responseData?: PayInResponse }).responseData;
      const errorMessage = getPayInErrorMessage(payInResponse?.errors, t);
      const errorKey = payInResponse?.errors?.errorKeys?.[0]?.translationKey;
      handleError(errorMessage, DEPOSIT_ERROR_STEP.PROCESSING_DEPOSIT as keyof typeof DEPOSIT_ERROR_STEP, undefined, errorKey);
    }
  }, [depositFlowInstance.createDepositTransactionMutation.isError, depositFlowInstance.createDepositTransactionMutation.error, handleError, t]);

  const resetForm = useCallback(() => {
    setDepositErrorStep(null);
    setDepositErrorMessage(null);
    setDepositErrorKey(undefined);
    setCurrentPaymentMethod('');
    setIsFirstStep(true);
    setIsTimedOut(false);
    setCurrentStatus('');
    clearRedirectionTimeout();
    redirectionTriggeredRef.current = false; // Reset redirection flag
    form.reset({
      amountStep: getAmountStepDefaultValues(),
      methodStep: getMethodStepInitialValues('', cardholderNameFromSession),
    });
    depositFlowInstance.resetFlow(); // Reset the deposit flow state
    onStateChange?.({
      isTransactionInProgress: false,
      isRedirectionInProgress: false,
      depositErrorStep: null,
    });
  }, [form, clearRedirectionTimeout, depositFlowInstance, onStateChange, getAmountStepDefaultValues, getMethodStepInitialValues, cardholderNameFromSession]);

  const handleTransactionStart = useCallback(() => {
    isTransactionInProgressRef.current = true;
    redirectionTriggeredRef.current = false; // Reset redirection flag for new transaction
    onStateChange?.({
      isTransactionInProgress: true,
      isRedirectionInProgress: false,
      depositErrorStep,
    });
  }, [depositErrorStep, onStateChange]);

  const setTrackingDetails = useCallback((options?: TrackingDetails) => {
    trackingRef.current = options || null;
  }, []);

  // Set the handleClose ref
  useEffect(() => {
    handleCloseRef.current = onClose;
  }, [onClose]);

  // Reset redirection flag when compoundId changes (new transaction)
  useEffect(() => {
    redirectionTriggeredRef.current = false;
  }, [depositFlowInstance.compoundId]);

  // Update ref when redirection status changes
  useEffect(() => {
    isRedirectionInProgressRef.current = depositFlowInstance.isRedirectionInProgress;
  }, [depositFlowInstance.isRedirectionInProgress]);

  // Update state when redirection status changes
  useEffect(() => {
    if (isTransactionInProgressRef.current) {
      onStateChange?.({
        isTransactionInProgress: true,
        isRedirectionInProgress: depositFlowInstance.isRedirectionInProgress,
        depositErrorStep,
      });
    }
  }, [depositFlowInstance.isRedirectionInProgress, depositErrorStep, onStateChange]);

  // Handle redirection
  useEffect(() => {
    if (depositFlowInstance.transactionRedirectionCheckData?.payload?.redirection?.url && !redirectionTriggeredRef.current) {
      if (!skipToStepRef.current || !setStatusRef.current) return;
      if (!depositFlowInstance.isTransactionInProgress) return;

      // Mark redirection as triggered to prevent multiple calls
      redirectionTriggeredRef.current = true;
      setStatusRef.current('redirection');
      skipToStepRef.current('redirection');
      startRedirectionTimeout();
    }
  }, [depositFlowInstance.transactionRedirectionCheckData?.payload?.redirection?.url, startRedirectionTimeout, depositFlowInstance.isTransactionInProgress]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearRedirectionTimeout();
    };
  }, [clearRedirectionTimeout]);

  const handleComplete = useCallback(async () => {
    try {
      // Reset form and close sheet
      onClose();
    } catch (error) {
      debug.error('Deposit error:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  const handleBackNavigation = useCallback(() => {
    if (backHandlerRef.current) {
      const handled = backHandlerRef.current();
      if (!handled) {
        // The handler returned false, meaning we should close the sheet
        onClose();
      }
    } else {
      // No handler registered, close the sheet
      onClose();
    }
  }, [onClose]);

  // Expose back handler to parent
  useEffect(() => {
    onBackHandlerReady?.(handleBackNavigation);
  }, [handleBackNavigation, onBackHandlerReady]);

  const registerBackHandler = useCallback((handler: () => boolean) => {
    backHandlerRef.current = handler;
  }, []);

  const registerCleanupHandler = useCallback((cleanup: () => void) => {
    cleanupHandlerRef.current = cleanup;
  }, []);

  const handleMultistepFormReadyInternal = useCallback((skipToStep: (stepId: string) => void, setStatus: (status: string) => void) => {
    skipToStepRef.current = skipToStep;
    setStatusRef.current = (status: string) => {
      setCurrentStatus(status);
      setStatus(status);
    };
  }, []);

  const handleRetry = useCallback(() => {
    // Clean up any active operations before retrying
    if (cleanupHandlerRef.current) {
      cleanupHandlerRef.current();
    }
    resetForm();
  }, [resetForm]);

  const getMethodStep = useCallback(() => {
    switch (currentPaymentMethod) {
      case 'Debit card':
        return (
          <CardDetailsStep
            onRegisterBackHandler={handler => {
              // When the sheet was opened with an auto-processed payment method, the amount
              // step was bypassed entirely. Navigating back to it would be confusing, so
              // return false to let handleBackNavigation close the sheet instead.
              registerBackHandler(autoProcessPaymentMethod ? () => false : handler);
            }}
            onError={handleError}
            onTransactionStart={handleTransactionStart}
            onRegisterCleanupHandler={registerCleanupHandler}
            setTrackingDetails={setTrackingDetails}
            bundleCode={bundleCode}
            namespace="global_sheets.deposit"
            depositFlowInstance={depositFlowInstance}
            data-track-container="Method"
          />
        );
      default:
        return null;
    }
  }, [
    currentPaymentMethod,
    registerBackHandler,
    autoProcessPaymentMethod,
    handleError,
    handleTransactionStart,
    registerCleanupHandler,
    setTrackingDetails,
    depositFlowInstance,
    bundleCode,
  ]);

  const getSteps = useMemo(
    () => [
      {
        id: 'amount',
        component: (
          <DepositAmountStep
            namespace="global_sheets.deposit"
            minimumAmountForBonus={minimumAmountForBonus}
            onError={handleError}
            onTransactionStart={handleTransactionStart}
            depositLimits={depositLimits ?? null}
            isDepositLimitsLoading={isDepositLimitsLoading}
            setTrackingDetails={setTrackingDetails}
            depositFlowInstance={depositFlowInstance}
            autoProcessPaymentMethod={autoProcessPaymentMethod}
            data-track-container="Amount"
          />
        ),
        schema: z.object({ ...amountSchema }),
      },
      {
        id: 'method_details',
        component: getMethodStep(),
        schema: z.object({ ...getMethodSchema(currentPaymentMethod) }),
      },
      {
        id: 'redirection',
        component: (
          <RedirectionStep
            namespace="global_sheets.deposit"
            depositFlowInstance={depositFlowInstance}
            {...(depositFlowInstance.depositType && { 'data-track-type': depositFlowInstance.depositType })}
            data-track-count="1"
            data-track-container="External Handover"
            {...(trackingRef.current?.method && { 'data-track-method': trackingRef.current.method })}
            {...(trackingRef.current?.provider && { 'data-track-provider': trackingRef.current.provider })}
            {...(trackingRef.current?.amount && { 'data-track-amount': trackingRef.current.amount })}
            {...(trackingRef.current?.transactionId && { 'data-track-id': trackingRef.current.transactionId })}
            {...(trackingRef.current?.offer && { 'data-track-offer': trackingRef.current.offer })}
          />
        ),
        schema: z.any(),
        condition: ({ status }: { formData: Record<string, unknown>; status: string }) => {
          return status === 'redirection';
        },
      },
      {
        id: 'deposit-limits',
        component: <DepositLimitsStep onRegisterBackHandler={registerBackHandler} depositLimits={depositLimits ?? null} />,
        schema: z.any(),
        condition: ({ status }: { formData: Record<string, unknown>; status: string }) => {
          return status === 'deposit-limits';
        },
      },
      {
        id: 'error',
        component: (
          <ErrorStep
            // TODO: Remove the comment in scope of adding the progress indicator
            // errorStep={depositErrorStep}
            errorMessage={depositErrorMessage || ''}
            errorKey={depositErrorKey}
            onRetry={handleRetry}
            data-track-category="❌❌❌"
            data-track-status={depositErrorStep === 'PROCESSING_DEPOSIT' ? 'Processing' : depositErrorStep === 'PAYMENT_SENT' ? 'Sent' : 'Complete'}
            data-track-error={depositErrorMessage || ''}
            {...(depositFlowInstance.depositType && { 'data-track-type': depositFlowInstance.depositType })}
            data-track-count="1"
            data-track-container="Failure Confirmation"
            {...(trackingRef.current?.method && { 'data-track-method': trackingRef.current.method })}
            {...(trackingRef.current?.provider && { 'data-track-provider': trackingRef.current.provider })}
            {...(trackingRef.current?.amount && { 'data-track-amount': trackingRef.current.amount })}
            {...(trackingRef.current?.transactionId && { 'data-track-id': trackingRef.current.transactionId })}
            {...(trackingRef.current?.offer && { 'data-track-offer': trackingRef.current.offer })}
          />
        ),
        schema: z.any(), // No validation needed for error step
        condition: ({ status }: { formData: Record<string, unknown>; status: string }) => {
          return status === 'error';
        },
      },
      {
        id: 'success',
        component: (
          <SuccessStep
            onClose={onClose}
            namespace="global_sheets.deposit"
            {...(depositFlowInstance.depositType && { 'data-track-type': depositFlowInstance.depositType })}
            data-track-count="1"
            data-track-container="Success Confirmation"
            {...(trackingRef.current?.method && { 'data-track-method': trackingRef.current.method })}
            {...(trackingRef.current?.provider && { 'data-track-provider': trackingRef.current.provider })}
            {...(trackingRef.current?.amount && { 'data-track-amount': trackingRef.current.amount })}
            {...(trackingRef.current?.transactionId && { 'data-track-id': trackingRef.current.transactionId })}
            {...(trackingRef.current?.offer && { 'data-track-offer': trackingRef.current.offer })}
          />
        ),
        schema: z.any(),
        condition: ({ status }: { formData: Record<string, unknown>; status: string }) => {
          return status === 'success' && !isOnGamesPage;
        },
      },
    ],
    [
      minimumAmountForBonus,
      handleError,
      handleTransactionStart,
      depositLimits,
      isDepositLimitsLoading,
      setTrackingDetails,
      depositFlowInstance,
      amountSchema,
      getMethodStep,
      getMethodSchema,
      currentPaymentMethod,
      registerBackHandler,
      depositErrorMessage,
      depositErrorKey,
      handleRetry,
      depositErrorStep,
      onClose,
      isOnGamesPage,
      autoProcessPaymentMethod,
    ]
  );

  const [currentStepId, setCurrentStepId] = useState<string | null>('amount');

  const handleStepChange = useCallback(
    (currentIsFirstStep: boolean) => {
      setIsFirstStep(currentIsFirstStep);
      onStepChange?.(currentIsFirstStep);
    },
    [onStepChange]
  );

  // Track step ID changes by monitoring the steps and current step index
  useEffect(() => {
    // Determine current step ID based on state
    let stepId: string | null = null;

    if (depositErrorStep) {
      stepId = 'error';
    } else if (currentStatus === 'success') {
      stepId = 'success';
    } else if (currentStatus === 'redirection' || isRedirectionInProgressRef.current) {
      stepId = 'redirection';
    } else if (!isFirstStep && currentPaymentMethod) {
      stepId = 'method_details';
    } else if (isFirstStep) {
      stepId = 'amount';
    }

    if (stepId !== currentStepId) {
      setCurrentStepId(stepId);
      onStepIdChange?.(stepId);
    }
  }, [isFirstStep, currentPaymentMethod, depositErrorStep, currentStatus, currentStepId, onStepIdChange]);

  return (
    <AccountSuspensionGate namespace={suspendedNotificationNamespaces.payments} variant="floating" className="p-5">
      <FormProvider {...form}>
        <MultistepForm
          key={openOnDepositLimits ? 'deposit-limits' : 'amount'}
          steps={getSteps}
          showProgressBar={false}
          showNavigation={false}
          onComplete={handleComplete}
          onStepChange={handleStepChange}
          onMultistepFormReady={handleMultistepFormReadyInternal}
          className={cn('px-6 py-8', className)}
          {...(openOnDepositLimits
            ? {
                initialStepId: 'deposit-limits',
                initialStatus: 'deposit-limits',
              }
            : {})}
        />
      </FormProvider>
    </AccountSuspensionGate>
  );
}

export { DepositFlowOrchestrator, type DepositFlowOrchestratorProps };
